/**
 * TabSyncManager - จัดการการ sync ข้อมูลระหว่าง tabs
 * ป้องกันการ sync ข้อมูลซ้ำเมื่อมีหลาย tabs เปิดอยู่
 */
class TabSyncManager {
    constructor() {
        this.channel = new BroadcastChannel('tab-sync-channel');
        this.isProcessing = false;
        this.processedRecords = new Set();
        
        // Listen for messages from other tabs
        this.channel.addEventListener('message', this.handleMessage.bind(this));
        
        // Clean up processed records periodically (10 minutes)
        setInterval(() => {
            this.cleanupProcessedRecords();
        }, 10 * 60 * 1000);
    }

    /**
     * ตรวจสอบว่า record นี้กำลังถูก process อยู่หรือไม่
     * @param {string} recordId - ID ของ record
     * @returns {boolean}
     */
    isRecordBeingProcessed(recordId) {
        return this.processedRecords.has(recordId) || 
               localStorage.getItem(`processing_${recordId}`) !== null;
    }

    /**
     * Lock record สำหรับการ process
     * @param {string} recordId - ID ของ record
     * @param {number} timeoutMs - Timeout ในการ lock (default: 30 วินาที)
     * @returns {boolean} - true ถ้า lock สำเร็จ, false ถ้ามี tab อื่น lock อยู่แล้ว
     */
    lockRecord(recordId, timeoutMs = 30000) {
        const lockKey = `processing_${recordId}`;
        const lockValue = `${Date.now()}_${Math.random()}`;
        
        // ตรวจสอบว่ามี lock อยู่แล้วหรือไม่
        const existingLock = localStorage.getItem(lockKey);
        if (existingLock) {
            const [timestamp] = existingLock.split('_');
            const lockAge = Date.now() - parseInt(timestamp);
            
            // ถ้า lock เก่าเกิน timeout ให้ลบทิ้ง
            if (lockAge > timeoutMs) {
                localStorage.removeItem(lockKey);
            } else {
                return false; // มี lock อยู่แล้ว
            }
        }

        // สร้าง lock ใหม่
        localStorage.setItem(lockKey, lockValue);
        this.processedRecords.add(recordId);
        
        // แจ้ง tabs อื่นว่ากำลัง process record นี้
        this.broadcastMessage({
            type: 'RECORD_LOCKED',
            recordId: recordId,
            timestamp: Date.now()
        });

        // ตั้ง timeout เพื่อปลด lock อัตโนมัติ
        setTimeout(() => {
            const currentLock = localStorage.getItem(lockKey);
            if (currentLock === lockValue) {
                this.unlockRecord(recordId);
            }
        }, timeoutMs);

        return true;
    }

    /**
     * ปลด lock record
     * @param {string} recordId - ID ของ record
     */
    unlockRecord(recordId) {
        const lockKey = `processing_${recordId}`;
        localStorage.removeItem(lockKey);
        this.processedRecords.delete(recordId);
        
        this.broadcastMessage({
            type: 'RECORD_UNLOCKED',
            recordId: recordId,
            timestamp: Date.now()
        });
    }

    /**
     * ส่งข้อความไปยัง tabs อื่น
     * @param {object} message - ข้อความที่จะส่ง
     */
    broadcastMessage(message) {
        try {
            this.channel.postMessage(message);
        } catch (error) {
            console.warn('Failed to broadcast message:', error);
        }
    }

    /**
     * จัดการข้อความที่ได้รับจาก tabs อื่น
     * @param {Event} event - Event ที่ได้รับ
     */
    handleMessage(event) {
        const { type, recordId, timestamp } = event.data;
        
        switch (type) {
            case 'RECORD_LOCKED':
                this.processedRecords.add(recordId);
                break;
            case 'RECORD_UNLOCKED':
                this.processedRecords.delete(recordId);
                break;
        }
    }

    /**
     * ทำความสะอาด processed records ที่เก่าแล้ว
     */
    cleanupProcessedRecords() {
        const cutoffTime = Date.now() - (10 * 60 * 1000); // 10 minutes ago
        
        // ทำความสะอาด localStorage
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('processing_')) {
                const value = localStorage.getItem(key);
                if (value) {
                    const [timestamp] = value.split('_');
                    if (parseInt(timestamp) < cutoffTime) {
                        localStorage.removeItem(key);
                    }
                }
            }
        });
    }

    /**
     * ปิดการเชื่อมต่อ
     */
    close() {
        this.channel.close();
    }
}

// Singleton instance
const tabSyncManager = new TabSyncManager();

export default tabSyncManager;